/**
 * ATHENA VOICE ENGINE 3.0 — Web Speech API Adapter
 *
 * Interface que futuros provedores STT devem implementar:
 *   onResult(transcript: string, isFinal: boolean)
 *   onEnd()
 *   onError(e: { error: string })
 *   start() → boolean
 *   destroy()
 */
export class WebSpeechAdapter {
  onResult = null;
  onEnd    = null;
  onError  = null;
  onEvent  = null;

  _rec  = null;
  _dead = false;

  constructor({ lang = 'pt-BR', continuous = false, interimResults = true } = {}) {
    this._lang = lang;
    this._continuous = continuous;
    this._interimResults = interimResults;
  }

  start() {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return false;

    const rec = new SR();
    rec.lang = this._lang;
    rec.continuous = this._continuous;
    rec.interimResults = this._interimResults;
    rec.maxAlternatives = 1;

    rec.onstart = () => { if (!this._dead) this.onEvent?.('recognition_start'); };
    rec.onaudiostart = () => { if (!this._dead) this.onEvent?.('audio_start'); };
    rec.onsoundstart = () => { if (!this._dead) this.onEvent?.('sound_start'); };
    rec.onspeechstart = () => { if (!this._dead) this.onEvent?.('speech_start'); };
    rec.onspeechend = () => { if (!this._dead) this.onEvent?.('speech_end'); };
    rec.onsoundend = () => { if (!this._dead) this.onEvent?.('sound_end'); };
    rec.onaudioend = () => { if (!this._dead) this.onEvent?.('audio_end'); };

    rec.onresult = (ev) => {
      if (this._dead) return;
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const r = ev.results[i];
        this.onResult?.(r[0]?.transcript || '', r.isFinal, { resultIndex: i });
      }
    };

    rec.onend   = () => { if (!this._dead) { this.onEvent?.('recognition_end'); this.onEnd?.(); } };
    rec.onerror = (e) => { if (!this._dead) { this.onEvent?.('recognition_error', e?.error || 'unknown'); this.onError?.(e); } };

    try {
      rec.start();
      this._rec = rec;
      return true;
    } catch {
      return false;
    }
  }

  destroy() {
    this._dead = true;
    if (this._rec) {
      try { this._rec.abort(); } catch {}
      this._rec = null;
    }
  }

  // Finaliza a sessão preservando o resultado final que o navegador emite antes
  // de `onend`. Diferente de destroy(), não invalida os callbacks atuais.
  stop() {
    if (this._rec) {
      try { this._rec.stop(); } catch {}
    }
  }
}
